import type { Coding } from "@medplum/fhirtypes";

/**
 * The `meta.security` label that marks a chat `Communication` as an internal note.
 *
 * FHIR carries disclosure restrictions as security labels on `Resource.meta.security`, drawn from
 * the HL7 Healthcare Privacy and Security Classification System. `NOPAT` is the obligation policy
 * "prohibition on disclosure to patient, family or caregivers without attending provider's
 * authorization", which is exactly what an internal note is. See
 * https://hl7.org/fhir/R4/security-labels.html and the v3 ActCode definition of `NOPAT`.
 *
 * The internal-note fact also lives in the `vibrant.message.internal-note` extension. The label
 * exists because an extension is not a FHIR search parameter, and an AccessPolicy criteria string
 * can only filter on what it can search. The patient-scope policy excludes this label with
 * `_security:not=`, so Medplum itself refuses to return an internal note to a patient session.
 *
 * Web and mobile both read it; only `Message.create` in `apps/web` writes it, together with the
 * extension, so the two facts cannot diverge.
 */
export const INTERNAL_NOTE_SECURITY_SYSTEM = "http://terminology.hl7.org/CodeSystem/v3-ActCode";
export const INTERNAL_NOTE_SECURITY_CODE = "NOPAT";

/** The Coding written into `meta.security` on every internal note. */
export const INTERNAL_NOTE_SECURITY_LABEL: Coding = {
  system: INTERNAL_NOTE_SECURITY_SYSTEM,
  code: INTERNAL_NOTE_SECURITY_CODE,
  display:
    "no disclosure to patient, family or caregivers without attending provider's authorization",
};

/**
 * The system-qualified `_security` search token.
 *
 * Always the qualified form, also for `_security:not=`. A bare `NOPAT` token matches the code
 * under any system, and in an AccessPolicy that is a security boundary, not style.
 */
export const INTERNAL_NOTE_SECURITY_QUERY = `${INTERNAL_NOTE_SECURITY_SYSTEM}|${INTERNAL_NOTE_SECURITY_CODE}`;

/** True when the security label list carries the internal-note label. */
export function hasInternalNoteSecurityLabel(labels: Coding[] | undefined): boolean {
  return !!labels?.some(
    (label) =>
      label.system === INTERNAL_NOTE_SECURITY_SYSTEM && label.code === INTERNAL_NOTE_SECURITY_CODE,
  );
}
